import React from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import LoginIcon from '@mui/icons-material/Login';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useNavigate } from 'react-router-dom';
import { StyledButton, StyledButtonBox } from '../index.styles';
import PasswordVerificationForm from 'src/components/PasswordVerificationForm';
import { toast } from 'react-toastify';
import { extractSchemaErrors, mapError } from 'src/utils/validation';
import {
  FormErrors,
  RegistrationReoSchema,
  firstPageFields,
  validateForm,
} from '../form';
import { client } from 'src/utils/api-client';

export default function RegistrationSecondPage(props: {
  form: RegistrationReoSchema;
  setForm: React.Dispatch<React.SetStateAction<RegistrationReoSchema>>;
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  onPrevious: () => void;
}) {
  const { form, setForm, errors, setErrors, onPrevious } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isError = (fieldName: keyof RegistrationReoSchema) =>
    !!form[fieldName] && !!mapError(errors, fieldName, form[fieldName]);
  const errorText = (fieldName: keyof RegistrationReoSchema) =>
    !!form[fieldName] &&
    String(t(mapError(errors, fieldName, form[fieldName]) || ''));

  const mutation = useMutation({
    mutationFn: () => client.post('/web/register_reo', form),
    onError: (err: any) => {
      toast.error(String(t('registration_bnp:formError')));
      const extractedErrors = extractSchemaErrors(err, form);
      setErrors(extractedErrors);
      if (
        Object.keys(extractedErrors).some((key) =>
          firstPageFields.includes(key as keyof RegistrationReoSchema)
        )
      ) {
        onPrevious();
      }
    },
    onSuccess: () => {
      toast.success(String(t('registration_bnp:success')));
      navigate('/login');
    },
  });

  return (
    <>
      <PasswordVerificationForm
        setNewPassword={(value: string) =>
          setForm({ ...form, password: value })
        }
        translationPrefix="registration"
      />

      {isError('password') && (
        <Alert sx={{ mt: '20px' }} severity="error">
          {errorText('password')}
        </Alert>
      )}

      <StyledButtonBox sx={{ mt: '50px', justifyContent: 'space-between' }}>
        <StyledButton
          variant="outlined"
          size="large"
          onClick={onPrevious}
          disabled={mutation.isLoading}
          startIcon={<NavigateBeforeIcon />}
        >
          {t('registration_bnp:previousPage')}
        </StyledButton>
        <StyledButton
          variant="contained"
          size="large"
          onClick={() => mutation.mutate()}
          disabled={
            Object.keys(validateForm(form)).length > 0 || mutation.isLoading
          }
          endIcon={<LoginIcon />}
        >
          {t('registration_bnp:register')}
        </StyledButton>
      </StyledButtonBox>
    </>
  );
}
